import { useEffect, useRef, useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";

const NetworkStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSlow, setIsSlow] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const wasOffline = useRef(!navigator.onLine);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);

      // Show success only if user was previously offline
      if (wasOffline.current) {
        setShowSuccess(true);
        wasOffline.current = false;

        setTimeout(() => {
          setShowSuccess(false);
        }, 2500);
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      wasOffline.current = true;
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Detect slow connection
    if (navigator.connection) {
      const connection = navigator.connection;

      const updateConnection = () => {
        setIsSlow(
          connection.effectiveType === "2g" ||
          connection.effectiveType === "slow-2g"
        );
      };

      updateConnection();
      connection.addEventListener("change", updateConnection);

      return () => {
        connection.removeEventListener("change", updateConnection);
        window.removeEventListener("online", handleOnline);
        window.removeEventListener("offline", handleOffline);
      };
    }

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // ✅ Success banner
  if (showSuccess) {
    return (
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          zIndex: 2000,
          bgcolor: "#4caf50",
          color: "#fff",
          textAlign: "center",
          py: 1,
        }}
      >
        <Typography variant="body2">
          ✅ Connection restored successfully
        </Typography>
      </Box>
    );
  }

  // ❌ Offline or 🟠 slow
  if (!isOnline || isSlow) {
    return (
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          zIndex: 2000,
          bgcolor: !isOnline ? "#f44336" : "#ff9800",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          py: 1,
        }}
      >
        {!isOnline && <CircularProgress size={18} color="inherit" />}
        <Typography variant="body2">
          {!isOnline
            ? "No internet connection. Trying to reconnect…"
            : "Your connection is slow"}
        </Typography>
      </Box>
    );
  }

  return null;
};

export default NetworkStatus;